import { useState, useEffect } from 'react';
import { Modal, Typography, Button, TextField } from '@mui/material';

type Props = {
    open: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => void;
    isEnabling: boolean;
    isProcessing: boolean;
};

export default function SyncStatusModal({ open, onClose, onConfirm, isEnabling, isProcessing }: Props) {
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setReason('');
            setError('');
        }
    }, [open]);

    const handleConfirm = () => {
        if (reason.length < 10) {
            setError('Reason must be at least 10 characters long.');
            return;
        }
        if (reason.length > 250) {
            setError('Reason must be less than 250 characters long.');
            return;
        }
        onConfirm(reason);
    };

    const handleClose = () => {
        if (!isProcessing) {
            setReason('');
            setError('');
            onClose();
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            className="sync-modal-overlay"
            disableScrollLock
        >
            <div className="custom-modal">
                <div className={`custom-modal__header ${isEnabling ? 'custom-modal__header--enabling' : 'custom-modal__header--disabling'}`}>
                    <Typography id="modal-modal-title" variant="h2" component="h2">
                        {isEnabling ? 'Enable Synchronization' : 'Disable Synchronization'}
                    </Typography>
                </div>

                <div className="custom-modal__content">
                    <Typography id="modal-modal-description">
                        Please provide a reason for {isEnabling ? 'enabling' : 'disabling'} synchronization for this store.
                        This action will be logged.
                    </Typography>

                    <TextField
                        fullWidth
                        label="Reason for change"
                        multiline
                        rows={3}
                        value={reason}
                        onChange={(e) => {
                            setReason(e.target.value);
                            if (error) setError('');
                        }}
                        error={!!error}
                        helperText={error || `${reason.length}/250 characters`}
                        inputProps={{ maxLength: 250 }}
                        variant="outlined"
                        placeholder="e.g., Scheduled maintenance completed..."
                        disabled={isProcessing}
                    />
                </div>

                <div className="custom-modal__actions">
                    <button onClick={handleClose} className="cancel-btn" disabled={isProcessing}>
                        Cancel
                    </button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        color={isEnabling ? "primary" : "warning"}
                        disabled={reason.length < 10 || isProcessing}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {isProcessing ? 'Processing...' : (isEnabling ? 'Enable Sync' : 'Disable Sync')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
